import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import {
  FiArrowLeft,
  FiBox,
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiChevronRight,
  FiExternalLink,
  FiFileText,
  FiGlobe,
  FiHome,
  FiMail,
  FiMapPin,
  FiPhone,
  FiRefreshCw,
  FiTruck,
  FiUploadCloud,
  FiUser,
  FiUsers,
  FiXCircle,
} from "react-icons/fi";
import {
  MdErrorOutline,
  MdOutlineBusiness,
  MdOutlineInventory2,
  MdOutlineLocalShipping,
  MdOutlineNoteAdd,
  MdOutlinePictureAsPdf,
  MdOutlineScale,
  MdOutlineShareLocation,
  MdOutlineVerifiedUser,
  MdOutlineWarehouse,
} from "react-icons/md";
import {
  getPartnerById,
  updatePartnerStatus,
  getPrivateFileBlob,
} from "../Services/partnerService";
import Skeleton from "../Components/SkeletonLoader";
import { toast } from "react-toastify";
import { RoutesPaths } from "../Router/constant";
import ConfirmationModal from "../Components/ConfirmationModal";
import { contractFileUpload } from "../Services/bookingService";

const capitalize = (value = "") =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    return value.split(",").map((v) => v.trim()).filter(Boolean);
  }
  return [];
};

const toHref = (url = "") => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

export default function PartnersDetail() {
  const fileInputRef = useRef(null);
  const [isContractUploaded, setIsContractUploaded] = useState(false);
  const { id } = useParams();
  const [singlePartnerData, setSinglePartnerData] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loadingAction, setLoadingAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const navigate = useNavigate();
  const [rejectReason, setRejectReason] = useState("");
  const [confirmation, setConfirmation] = useState({
    show: false,
    type: "", // "accept" or "reject"
  });

  useEffect(() => {
    if (id) fetchPartnerData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleViewClick = () => {
    navigate(RoutesPaths.FREIGHTAGREEMENT, {
      state: { singlePartnerData },
    });
  };

  const partnerId = singlePartnerData?._id;
  const handleView = async (filename) => {
    const newTab = window.open("", "_blank");

    try {
      const res = await getPrivateFileBlob(filename);

      const blob = new Blob([res.data], {
        type: res.headers?.["content-type"] || "application/pdf",
      });

      const url = URL.createObjectURL(blob);
      newTab.location.href = url;

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      newTab?.close();
      alert("Unable to open document (token invalid or file missing).");
    }
  };

  // `initial` shows the full-page loader; refreshes after an action keep the page visible.
  const fetchPartnerData = async (initial = false) => {
    if (initial) {
      setLoading(true);
      setLoadError(false);
    }
    try {
      const res = await getPartnerById(id);
      const user = res?.data?.data?.user;
      setSinglePartnerData(user);
      setServiceTypes(user?.serviceTypes || []);
      if (initial && !user) setLoadError(true);
    } catch (error) {
      console.error("Error fetching user:", error);
      if (initial) setLoadError(true);
    } finally {
      if (initial) setLoading(false);
    }
  };

  const handleOpenAcceptModal = () => {
    setConfirmation({ show: true, type: "accept" });
  };

  const handleOpenRejectModal = () => {
    setRejectReason("");
    setConfirmation({ show: true, type: "reject" });
  };

  const handleCloseModal = () => {
    setConfirmation({ show: false, type: "" });
    setRejectReason("");
  };

  const handleConfirmAction = async () => {
    try {
      setLoadingAction(confirmation.type);
      const status = confirmation.type === "accept" ? "accepted" : "rejected";

      const res = await updatePartnerStatus(id, status, rejectReason);

      toast.success(res.data.message);
      await fetchPartnerData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update vendor status.");
    } finally {
      setLoadingAction(null);
      handleCloseModal();
    }
  };

  const formatDocumentTitle = (key) => {
    const titleMap = {
      commercialInvoice: "Commercial Invoice",
      customsImportDeclaration: "Customs Import Declaration",
      declarationOfCustomsValue: "Declaration of Customs Value (ADV)",
      loadingCertificate: "Loading Certificate (ARC / Waiver / CNCA)",
      ministryOfIndustryLicense:
        "Ministry of Industry and Trade (MINCO) License",
      packingList: "Packing List",
      terminalHandlingReceipts: "Terminal Handling Receipts",
      solasCertificate: "SOLAS Certificate",
    };
    return titleMap[key] || capitalize(key.replace(/([A-Z])/g, " $1").trim().toLowerCase());
  };

  const documentEntries = singlePartnerData?.documents
    ? Object.entries(singlePartnerData.documents).map(([key, value]) => ({
        title: formatDocumentTitle(key),
        file: value,
        type: value?.split(".").pop()?.toUpperCase() || "N/A",
      }))
    : [];

  const [contractFile, setContractFile] = useState();
  const [contractLoader, setContractLoader] = useState(true);
  const [contractLoading, setContractLoading] = useState(false);

  const handleContractFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";

    if (!isImage && !isPdf) {
      toast.error("Only images or PDF files are allowed");
      return;
    }

    setContractFile(file);
    setIsContractUploaded(true);
    setContractLoader(false);
  };

  const handleContractSubmit = async () => {
    try {
      setContractLoading(true);
      const response = await contractFileUpload(partnerId, contractFile);
      toast.success(response?.data?.message);
      fetchPartnerData();
      setIsContractUploaded(false);
      setContractLoader(true);
    } catch (err) {
      console.error("File upload failed:", err);
      toast.error(err?.response?.data?.data);
      setContractLoader(true);
      setContractLoading(false);
    } finally {
      setContractLoading(false);
    }
  };

  const handleContractCancel = () => {
    setContractFile(" ");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setIsContractUploaded(false);
    setContractLoader(true);
  };

  /* =========================================
     DERIVED VIEW DATA
  ========================================= */

  const partner = singlePartnerData || {};
  const status = String(partner.status || "").toLowerCase();
  const contractStatus = partner?.partnershipContract?.status || "";
  const services = toList(partner.serviceTypes);
  const coverage = toList(partner.geographicCoverageAreas);
  const fleet = partner.fleetCapacity ?? [];
  const isBusy = loadingAction === "accept" || loadingAction === "reject";
  const phone = partner.phone
    ? `${partner.countryCode ? `(${partner.countryCode}) ` : ""}${partner.phone}`
    : "";

  const infoItems = [
    { label: "Company name", Icon: MdOutlineBusiness, value: partner.companyName },
    { label: "Business registration number / Tax ID", Icon: FiFileText, value: partner.businessRegistrationNumber },
    {
      label: "Service types",
      Icon: FiBox,
      value: services.length ? (
        <span className="qpd-chips">
          {services.map((s) => (
            <span className="qs-type" key={s}>{s}</span>
          ))}
        </span>
      ) : null,
    },
    { label: "Email address", Icon: FiMail, value: partner.email },
    { label: "Headquarters address", Icon: FiMapPin, value: partner?.headquartersAddress?.address },
    { label: "Phone number", Icon: FiPhone, value: phone },
    { label: "Contact person name", Icon: FiUser, value: partner.contactPersonName },
    { label: "Contact person title", Icon: FiBriefcase, value: partner.contactPersonTitle },
    {
      label: "Website",
      Icon: FiGlobe,
      value: partner.website ? (
        <a className="qpd-link" href={toHref(partner.website)} target="_blank" rel="noopener noreferrer">
          {partner.website} <FiExternalLink />
        </a>
      ) : null,
    },
    { label: "Number of employees", Icon: FiUsers, value: partner.numberOfEmployees },
    { label: "Year of experience", Icon: FiCalendar, value: partner.yearOfExperience },
    { label: "Geographic coverage areas", Icon: MdOutlineShareLocation, value: coverage.join(", ") },
  ];

  const renderSectionHead = (Icon, title, subtitle) => (
    <div className="qv-section-head">
      <span className="qv-section-icon">
        <Icon />
      </span>
      <div>
        <h6>{title}</h6>
        <p>{subtitle}</p>
      </div>
    </div>
  );

  const renderValueBox = (Icon, label, value) => (
    <div className="qpd-value-box">
      <span className="qpd-value-icon">
        <Icon />
      </span>
      <div className="qpd-value-body">
        <span>{label}</span>
        <div className="qpd-value">{value || "--"}</div>
      </div>
    </div>
  );

  /* =========================================
     LOADER / ERROR
  ========================================= */

  const breadcrumb = (
    <nav className="qd-breadcrumb">
      <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
        <FiHome />
      </NavLink>
      <FiChevronRight />
      <NavLink to={RoutesPaths.PARTNERS}>Vendors</NavLink>
      <FiChevronRight />
      <span className="qd-breadcrumb-current">Vendor id : {id}</span>
    </nav>
  );

  if (loading) {
    return (
      <div className="container-fluid qd-page qpd-page" aria-busy="true">
        {breadcrumb}
        <div className="qd-card qpd-hero">
          <div className="qd-skel" style={{ width: 84, height: 84, borderRadius: "50%" }} />
          <div>
            <Skeleton width={240} height={28} />
            <div style={{ height: 8 }} />
            <Skeleton width={200} height={16} />
            <div style={{ height: 8 }} />
            <Skeleton width={90} height={24} />
          </div>
        </div>
        <section className="qd-card qv-section">
          <div className="qv-section-head">
            <div className="qd-skel" style={{ width: 52, height: 52, borderRadius: 14 }} />
            <div>
              <Skeleton width={200} height={20} />
              <div style={{ height: 6 }} />
              <Skeleton width={300} height={14} />
            </div>
          </div>
          <div className="qpd-info-grid">
            {[...Array(12)].map((_, i) => (
              <div className="qpd-info-row" key={i}>
                <div className="qd-skel" style={{ width: 36, height: 36, borderRadius: 10 }} />
                <Skeleton width={140} height={14} />
                <Skeleton width={160} height={14} />
              </div>
            ))}
          </div>
        </section>
        <div className="qpd-two-col">
          {[0, 1].map((i) => (
            <section className="qd-card qv-section" key={i}>
              <div className="qv-section-head">
                <div className="qd-skel" style={{ width: 52, height: 52, borderRadius: 14 }} />
                <Skeleton width={180} height={20} />
              </div>
              <div style={{ padding: "0 20px" }}>
                <div className="qd-skel" style={{ height: 56 }} />
              </div>
            </section>
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="container-fluid qd-page qpd-page">
        {breadcrumb}
        <div className="qd-card qd-panel">
          <div className="qu-empty">
            <span className="qu-empty-art">
              <MdErrorOutline />
            </span>
            <h6>Unable to load vendor</h6>
            <p>The vendor could not be found or there was a problem loading it.</p>
            <div className="qd-bookings-tools">
              <NavLink to={RoutesPaths.PARTNERS} className="qd-btn qd-btn-outline">
                <FiArrowLeft /> Back to vendors
              </NavLink>
              <button type="button" className="qd-btn qd-btn-primary" onClick={() => fetchPartnerData(true)}>
                <FiRefreshCw /> Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid qd-page qpd-page">
      {breadcrumb}

      {/* Hero */}
      <div
        className="qd-card qpd-hero"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/img/LogisticsRoute.png)` }}
      >
        <div className="qpd-avatar">
          {(partner.companyName || "?").trim().charAt(0).toUpperCase()}
        </div>
        <div className="qpd-identity">
          <h1>{partner.companyName || "--"}</h1>
          {partner.email && (
            <p>
              <FiMail /> {partner.email}
            </p>
          )}
          {status && (
            <span className={`qc-status qp-status-${status}`}>
              <span className="qu-dot" />
              {capitalize(status)}
            </span>
          )}
        </div>
      </div>

      {/* Company information */}
      <section className="qd-card qv-section">
        {renderSectionHead(
          MdOutlineBusiness,
          "Company information",
          "Basic details about the vendor's company and contact information.",
        )}
        <div className="qpd-info-grid">
          {infoItems.map(({ label, Icon, value }) => (
            <div className="qpd-info-row" key={label}>
              <span className="qpd-info-icon">
                <Icon />
              </span>
              <span className="qpd-info-label">{label}</span>
              <span className="qpd-info-value">{value || value === 0 ? value : "--"}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Fleet + warehousing */}
      <div className="qpd-two-col">
        <section className="qd-card qv-section">
          {renderSectionHead(FiTruck, "Fleet capacity", "Details about the vendor's vehicle fleet.")}
          <div className="qpd-section-body">
            {fleet.length > 0 ? (
              fleet.map((item, idx) => (
                <div className="qpd-fleet-row" key={idx}>
                  {renderValueBox(MdOutlineLocalShipping, "Vehicle type", item?.vehicleType)}
                  {renderValueBox(MdOutlineInventory2, "Quantity", item?.qty)}
                  {renderValueBox(MdOutlineScale, "Capacity", item?.capacity)}
                </div>
              ))
            ) : (
              <p className="qpd-empty-text">No fleet capacity added</p>
            )}
          </div>
        </section>

        <section className="qd-card qv-section">
          {renderSectionHead(
            MdOutlineWarehouse,
            "Warehousing capacity",
            "Details about the vendor's warehousing capacity.",
          )}
          <div className="qpd-section-body">
            {renderValueBox(MdOutlineWarehouse, "Warehousing capacity", partner.warehousingCapacity)}
          </div>
        </section>
      </div>

      {/* Licensing */}
      <section className="qd-card qv-section">
        {renderSectionHead(MdOutlineVerifiedUser, "Licensing and compliance", "Vendor verification documents.")}
        <div className="qpd-section-body">
          {documentEntries.length > 0 ? (
            <div className="qpd-doc-grid">
              {documentEntries.map((doc, i) => (
                <div key={i} className="qpd-doc">
                  <span className="qpd-doc-icon">
                    <MdOutlinePictureAsPdf />
                  </span>
                  <span className="qpd-doc-text">
                    <strong>{doc.title}</strong>
                    <small>File format: {doc.type}</small>
                  </span>
                  <button
                    type="button"
                    className="qd-btn qd-btn-sm qpd-view-btn"
                    onClick={() => handleView(doc.file)}
                    disabled={!doc.file}
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="qpd-empty-text">No documents uploaded.</p>
          )}
        </div>
      </section>

      {/* Contract (accepted vendors only) */}
      {status === "accepted" && serviceTypes.length > 0 && (
        <section className="qd-card qv-section qpd-contract">
          <div className="qv-section-head qpd-contract-head">
            <span className="qv-section-icon">
              <MdOutlineNoteAdd />
            </span>
            <div>
              <h6>
                Upload contract
                {partner?.uploadPartnerContractPDF && contractStatus && (
                  <span className={`qc-status qp-status-${contractStatus} qpd-contract-status`}>
                    <span className="qu-dot" />
                    {capitalize(contractStatus)}
                  </span>
                )}
              </h6>
              <p>
                {partner?.uploadPartnerContractPDF
                  ? "The freight contract is available. The vendor reads it and signs to proceed."
                  : "Upload the freight contract here to proceed with the vendor contract."}
              </p>
              {contractStatus === "rejected" && partner?.partnershipContract?.rejectionReason && (
                <p className="qpd-reject-reason">
                  <strong>Rejection message:</strong> {partner.partnershipContract.rejectionReason}
                </p>
              )}
            </div>
          </div>

          <div className="qpd-section-body">
            {(contractStatus === "" || contractStatus === "pending") && (
              <>
                <div className={`qpd-doc qpd-contract-upload ${isContractUploaded ? "has-file" : ""}`}>
                  <label htmlFor="uploadQuotation" className="qpd-contract-label">
                    <span className="qpd-doc-icon">
                      {isContractUploaded ? <FiCheck /> : <FiUploadCloud />}
                    </span>
                    <span className="qpd-doc-text">
                      <strong>Contract (Upload pdf, jpg, png)</strong>
                      <small>
                        {isContractUploaded && contractFile?.name
                          ? contractFile.name
                          : "Click to choose a file · File size: 5mb"}
                      </small>
                    </span>
                  </label>

                  {partner?.uploadPartnerContractPDF && (
                    <button type="button" className="qd-btn qd-btn-sm qpd-view-btn" onClick={handleViewClick}>
                      View
                    </button>
                  )}

                  <input
                    id="uploadQuotation"
                    type="file"
                    name="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleContractFileChange}
                    disabled={partner?.isAcceptedQuotation === true}
                    accept="image/*,application/pdf"
                  />
                </div>

                <div className="qpd-contract-actions">
                  <button
                    type="button"
                    className="qd-btn qd-btn-outline"
                    disabled={contractLoader}
                    onClick={handleContractCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="qd-btn qd-btn-primary"
                    disabled={contractLoader || contractLoading}
                    onClick={handleContractSubmit}
                  >
                    {contractLoading ? (
                      <>
                        <span className="qo-spinner" /> Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </>
            )}

            {["accepted", "rejected"].includes(contractStatus) && (
              <div className="qpd-doc">
                <span className="qpd-doc-icon">
                  <MdOutlinePictureAsPdf />
                </span>
                <span className="qpd-doc-text">
                  <strong>Freight contract</strong>
                  <small>Contract file provided by Quimera team.</small>
                </span>
                <button type="button" className="qd-btn qd-btn-sm qpd-view-btn" onClick={handleViewClick}>
                  View contract
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Footer actions */}
      <div className="qv-actions qpd-actions">
        <button type="button" className="qd-btn qv-cancel-btn" onClick={() => navigate(RoutesPaths.PARTNERS)}>
          <FiArrowLeft /> Back
        </button>

        {status === "rejected" ? (
          <button type="button" className="qd-btn qd-btn-outline" disabled>
            <FiXCircle /> Cancelled Request
          </button>
        ) : status !== "accepted" ? (
          <div className="qd-bookings-tools">
            <button
              type="button"
              className="qd-btn qp-delete-btn qpd-action-btn"
              onClick={handleOpenRejectModal}
              disabled={isBusy}
            >
              {loadingAction === "reject" ? "Processing..." : "Reject request"}
            </button>
            <button
              type="button"
              className="qd-btn qd-btn-primary qpd-action-btn"
              onClick={handleOpenAcceptModal}
              disabled={isBusy}
            >
              {loadingAction === "accept" ? "Processing..." : "Accept request"}
            </button>
          </div>
        ) : null}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={confirmation.show}
        title={confirmation.type === "accept" ? "Confirm acceptance" : "Confirm rejection"}
        onConfirm={handleConfirmAction}
        onCancel={handleCloseModal}
        confirmText={confirmation.type === "accept" ? "Yes, accept" : "Yes, reject"}
        confirmDisabled={isBusy}
        cancelText="Cancel"
      >
        {confirmation.type === "accept" ? (
          <p>Are you sure you want to accept this vendor request?</p>
        ) : (
          <>
            <p>Are you sure you want to reject this vendor request?</p>
            <textarea
              className="qb-modal-input"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              rows="4"
            />
          </>
        )}
      </ConfirmationModal>
    </div>
  );
}
